/* State, DOM refs, and shared utilities */

import { signal } from "./store.js";

export const state = {
  // ── Session registry ────────────────────────────────────────────────────
  // Each entry is keyed by conversation/session ID and holds a freshSession()
  // sub-state object. The proxy below forwards PER_SESSION_FIELDS reads/writes
  // to state.sessions[state.activeSessionId].
  sessions: {},
  activeSessionId: "default",

  // ── Global (not per-session) ─────────────────────────────────────────────
  tenant: null,
  tenants: [],
  tenantDropdownOpen: false,
  // true while a tenant login/switch is in flight -- tenants.js's switchTenant() uses this
  // as its own re-entrancy guard (a second switch call bails out early while one is already
  // running). No longer carries the loader-overlay's from/to/phase payload (the overlay was
  // removed -- the sidebar conn-card dot + a failure toast are the only progress/error
  // indicators now); it's a plain boolean.
  tenantSwitching: false,
  // true from the START of a tenant login/switch attempt (tenants.js's switchTenant()/
  // submitLogin(), and the boot-time initial connect in main.js's initV2Shared()) until it
  // resolves (success or failure) -- drives the sidebar conn-card dot's third CONNECTING
  // state (shell-v2.js's updateConnDot()). Deliberately separate from tenantSwitching above
  // (isn't set at the true start of submitLogin(), which shows its own "Connecting..." button
  // state before tenantSwitching is set) so this field's start/end points can be pinned
  // exactly where the dot needs them.
  tenantConnecting: false,
  // Hosted connection health for the sidebar conn-card dot: "ok" | "connecting" | "off".
  // Reactive (REACTIVE_FIELDS below) so shell-v2.js's mode-aware dot effect re-renders on
  // change. Distinct from tenantConnecting (which drives the dot in LOCAL mode only).
  connHealth: "off",
  // True while an in-place hosted connection switch runs -- tenants.js's switchConnection()
  // re-entrancy guard. Plain (non-reactive): nothing subscribes to it; it's a guard flag only.
  connSwitching: false,
  tenantWorkspaceLoading: false,  // true while a workspace JSON is being restored. Forward-looking guard for
                                  // future auto-save hooks (chat-message, tab-close, etc.) — they should
                                  // skip persistence when this is true to avoid clobbering the just-loaded state.
  fileTree: null,
  syncStatuses: [],
  syncStatusMap: new Map(),
  wsConnected: false,
  healthOk: false,
  diffPanelOpen: false,
  diffFiles: [],
  expandedDiffNames: new Set(),     // Sublime-Merge style: which file cards are expanded
  diffCache: new Map(),             // `${kind}/${name}` → cached unified-diff response
  openFolders: new Set(),
  conversations: [],         // loaded from server
  activeConversationId: null, // id of the loaded history conversation, null = current session
  savedConversations: [],     // cached list from GET /api/conversations
  wsCtxMenuOpen: false,
  wsOverflowOpen: false,
  _fileChangeTimer: null,
  syncLoading: false,
  syncErrors: {},   // {kind: errorMessage} — per-kind sync failures
  // true when the MOST RECENT loadSyncStatuses() (file-tree.js) attempt threw,
  // false once one succeeds. Lets consumers (v2's Sync/Templates/Quote-tables
  // views) distinguish a genuinely-empty/in-sync result from a failed fetch,
  // which loadSyncStatuses() itself only console.warn()s about (no throw, no
  // other readable signal) — see that function for where this is set.
  syncStatusLoadFailed: false,
  scriptMeta: {},          // {[name]: {type, isActive, isModule, isRest, eventCount, modifiedOn}}
  templateMeta: {},        // {[name]: {pageName, slotName, templateId, isOverride, ...}}
  pinnedArtifacts: new Set(),  // Set of "kind:name" — kind is "global-scripts" | "custom-calculations" | "ui-templates"

  // Zone layout state
  layoutConfig: null,
  bottomZoneVisible: false,
  rightZoneVisible: true,
  leftZoneVisible: true,
  panelZoneMap: {},          // { panelId: zoneId } — which zone owns each panel
  zoneActivePanels: {},      // { zoneId: panelId } — active panel per zone
  zoneSplits: {},            // { zoneId: { size, collapsed } } — per-zone sizing
};

// ---------------------------------------------------------------------------
// Reactive bridge
// ---------------------------------------------------------------------------
//
// Wrap "hot" fields of `state` in signal-backed accessors so existing
// `state.foo` reads/writes keep working unchanged — but writes now notify
// subscribers. Modules opt in to reactivity by importing `signals` and using
// `.subscribe()` / `bind()` / `computed()` from store.js; code that only
// reads/writes `state.foo` needs no changes.
//
// IMPORTANT: signals use identity (Object.is) equality. For Map/Set/object
// values, assign a FRESH value to notify — mutating in place will not fire.
//
// ── Three state regimes — know which one a field is in BEFORE wiring to it ──
//   1. Signal-backed globals — REACTIVE_FIELDS (below). `state.<f>` routes through
//      `signals.<f>`; a FRESH assignment notifies bind()/effect()/.subscribe().
//      Use for app-wide state the UI should track automatically.
//   2. Per-session fields — PER_SESSION_FIELDS (further down). `state.<f>` is a live
//      view onto the ACTIVE session's sub-state via the proxy; switching sessions
//      swaps the value but notifies NOTHING. Do NOT bind()/effect() these (they
//      won't re-run on a switch) — panels showing per-session data re-render via the
//      'session-switch' listener in main.js.
//   3. Plain fields — everything else (openFolders, diffCache, expandedDiffNames, …):
//      not reactive; consumers call a render fn explicitly. Mutating in place is fine
//      *because* nothing subscribes — but if you make one reactive, switch to fresh
//      assignment (regime 1's rule) or the update will silently not fire.

export const signals = {};

// Fields promoted to signals. Initial values are captured from the literal
// above, so there is no duplicated default to drift.
const REACTIVE_FIELDS = [
  "tenant",
  "tenantConnecting",
  "connHealth",
  "fileTree",
  "syncStatuses",
  "syncStatusMap",
  "syncLoading",
  "syncErrors",
  "syncStatusLoadFailed",
  "scriptMeta",
  "templateMeta",
  "wsConnected",
  // NOTE: fieldsTree + pipelineData are PER-SESSION (see PER_SESSION_FIELDS),
  // not signal-backed here. The exec-order liveness keys off `codebaseRevision`,
  // not these fields.
];

for (const key of REACTIVE_FIELDS) {
  signals[key] = signal(state[key]); // seed from the literal's current value
  Object.defineProperty(state, key, {
    get() {
      return signals[key].peek(); // plain reads do NOT subscribe
    },
    set(v) {
      signals[key].set(v); // legacy writes notify subscribers
    },
    enumerable: true,
    configurable: true,
  });
}

// New reactive fields with no prior slot in the literal.
signals.codebaseRevision = signal(0); // monotonic; bumped when code on disk changes
signals.agentFileWrites = signal({}); // path -> { ts, byAgent, conflict } (assign fresh to notify)
for (const key of ["codebaseRevision", "agentFileWrites"]) {
  Object.defineProperty(state, key, {
    get() {
      return signals[key].peek();
    },
    set(v) {
      signals[key].set(v);
    },
    enumerable: true,
    configurable: true,
  });
}

// ---------------------------------------------------------------------------
// Per-session proxy helpers
// ---------------------------------------------------------------------------

// Fields that live per chat session. The proxy loop below forwards
// `state.<field>` reads/writes to the active session's sub-state.
export const PER_SESSION_FIELDS = [
  "messages",
  "isThinking",
  "inputLocked",
  "pendingAttachments",
  "cfgPending",
  "cfgInFlight",
  "cfgConfigId",
  "cfgTraces",
  "cfgTracePanelOpen",
  "cfgTraceFilter",
  "cfgLabelFilter",
  "qteQuoteId",
  "workspaceTabs",
  "activeWorkspaceTab",
  "wsTabIdCounter",
  "pipelineData",
  "pipelineScope",
  "pipelineLoading",
  "fieldsTree",
  "fieldsTreeSource",
  "fieldCalcsLoading",
];

/** A fresh per-session sub-state with default values. */
// Resume Phase 0 seq gate (pure, unit-tested): apply a frame iff it is unnumbered (local
// /ws/chat carries no seq) or strictly newer than the session's high-water mark. Duplicate
// or replayed frames (seq <= lastSeq) are dropped, which is what makes replay idempotent.
export function shouldApplySeq(lastSeq, seq) {
  if (seq == null) return true;
  return seq > (lastSeq || 0);
}

// Per-run gate reset (pure, unit-tested): the seq high-water mark is only meaningful
// WITHIN one run, and only the tab that sends a turn resets it — so a tab that did not
// send (fan-out viewing, a resumed conversation another device advanced) would see the
// next run's frames restart at seq 1 under a stale high mark and silently drop them all.
// Track the run identity per session: returns true when *runId* differs from the run
// last tracked (the caller must zero lastSeq before the seq gate runs). run_id-less
// frames (local /ws/chat, a run's pre-identity window) never reset and never re-track.
export function noteRunFrame(sess, runId) {
  if (runId == null) return false;
  if (sess.lastRunId === runId) return false;
  sess.lastRunId = runId;
  return true;
}

export function freshSession() {
  return {
    messages: [],
    isThinking: false,
    inputLocked: false,
    lastSeq: 0,           // per-run seq high-water mark (resume Phase 0; reset per turn)
    lastRunId: null,      // the run those seqs belong to (a new run reopens the gate)
    pendingAttachments: [],
    cfgPending: [],
    cfgInFlight: false,
    cfgConfigId: null,
    cfgTraces: [],
    cfgTracePanelOpen: false,
    cfgTraceFilter: "script",
    cfgLabelFilter: "",
    qteQuoteId: null,
    workspaceTabs: [],
    activeWorkspaceTab: null,
    wsTabIdCounter: 0,
    pipelineData: null,
    pipelineScope: "item-calcs",
    pipelineLoading: false,
    fieldsTree: null,
    fieldsTreeSource: null,
    fieldCalcsLoading: {},
  };
}

/**
 * Get — or lazily create — the per-session sub-state for an explicit
 * conversation id. The single source of truth for "give me this session's
 * state": WS handlers route through it so an event for a not-yet-registered
 * (e.g. server-spawned) session creates + buffers instead of being dropped.
 */
export function getOrCreateSession(convId) {
  let s = state.sessions[convId];
  if (!s) {
    s = freshSession();
    state.sessions[convId] = s;
  }
  return s;
}

/**
 * Read a session's seq high-water mark WITHOUT creating the session — the replay
 * cursor the transport presents in the hosted resume frame on socket open. A
 * conversation with no session yet (fresh reload) resumes from 0 (full replay).
 */
export function sessionLastSeq(convId) {
  return (convId && state.sessions[convId]?.lastSeq) || 0;
}

/**
 * Return the sub-state for the currently active session (lazily created).
 */
export function currentSession() {
  return getOrCreateSession(state.activeSessionId);
}

/**
 * Activate a session by ID.
 * Ensures a sub-state entry exists and returns it.
 * @param {string} convId
 * @returns {object} the session sub-state
 */
export function setActiveSession(convId) {
  state.activeSessionId = convId;
  return currentSession();
}

/**
 * Spawn a brand-new session (new unique ID), make it active, and return its sub-state.
 * @param {object} [opts] — reserved for future use (e.g. { inherit: false })
 * @returns {object} the new session sub-state
 */
export function spawnSession(opts) { // eslint-disable-line no-unused-vars
  const id = (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
  state.activeSessionId = id;
  const s = freshSession();
  state.sessions[id] = s;
  return s;
}

// Install getter/setter proxy for every per-session field onto `state`.
// Existing call sites (state.cfgConfigId = "x") transparently operate on the
// active session's sub-state without any changes at the call site.
for (const f of PER_SESSION_FIELDS) {
  Object.defineProperty(state, f, {
    get() { return currentSession()[f]; },
    set(v) { currentSession()[f] = v; },
    configurable: true,
    enumerable: true,
  });
}

// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

export const $ = (sel) => document.querySelector(sel);
export const $$ = (sel) => document.querySelectorAll(sel);

export const refs = {};

export function cacheRefs() {
  [
    // Note: some IDs are created dynamically by _populate*Panel() in main.js
    // and re-cached there. IDs listed here are from index.html static elements
    // or kept for null-guarded backward compat.
    "tenantName", "connDot",
    "btnRefreshSync",
    "fileTree", "conversationList", "chatStream", "chatWelcome",
    "promptInput", "btnSend", "modelSelect", "healthChip",
    "confirmDialog", "confirmTitle", "confirmMessage", "confirmCancel", "confirmOk",
    "confirmTypedDialog", "confirmTypedTitle", "confirmTypedMessage",
    "confirmTypedInput", "confirmTypedCancel", "confirmTypedOk",
    "usageDialog", "usageTitle", "usageMessage", "usageCancel", "usageOk",
    "btnNewConversation", "btnSaveConversation",
    "atAutocomplete",
    "tenantSwitcher", "tenantDropdown", "tenantList", "btnAddTenant",
    "loginDialog", "loginUrl", "loginAuthMode", "loginPassFields", "loginCcFields",
    "loginUsername", "loginPassword", "loginClientId", "loginClientSecret",
    "loginRemember", "loginError", "loginCancel", "loginSubmit",
    "app",
    "searchDialog", "searchInput", "searchResults", "searchPreview",
    "searchPreviewHeader", "searchPreviewFilename", "searchPreviewPath",
    "searchPreviewBody", "searchStats", "searchCloseBtn",
    "searchToggleRegex", "searchToggleCase", "searchSplitHandle",
    "searchOpenInTab",
    "scaffoldDialog", "scaffoldPage", "scaffoldSlot", "scaffoldName",
    "scaffoldError", "scaffoldPreview", "scaffoldCancel", "scaffoldSubmit",
    "nameConfirmDialog", "nameConfirmTitle", "nameConfirmDesc",
    "nameConfirmInput", "nameConfirmCancel", "nameConfirmOk",
    "templatePickerDialog", "templatePickerList",
    "templatePickerCancel", "templatePickerOk",
    "wsCtxMenu", "wsOverflowPanel", "wsOverflowBtn",
    "fileViewer",
    "activityBar",
    "sbSearchInput", "sbSearchResults", "sbSearchStats",
    "sbToggleRegex", "sbToggleCase",
    "pipelineBody", "btnRefreshPipeline",
    "tabItemCalcs", "tabQuoteEvents",
    // Zone layout refs
    "topNav", "tenantSelector", "syncBadges",
    "navAvatar", "navConn",
    "zoneLeft", "zoneCenter", "zoneRight", "zoneBottom",
    "zoneLeftTabs", "zoneCenterTabs", "zoneRightTabs", "zoneBottomTabs",
    "zoneLeftContent", "zoneCenterContent", "zoneRightContent", "zoneBottomContent",
    "resizeLeft", "resizeRight", "resizeBottom",
    "statusBar", "sbBranch", "sbChanges", "sbSyncIndicator",
    "sbConsoleToggle", "sbCursor", "sbIndent", "sbLang",
    "btnGlobalSearch", "btnSettings",
  ].forEach((id) => { refs[id] = document.getElementById(id); });
}

// ---------------------------------------------------------------------------
// Shared utilities
// ---------------------------------------------------------------------------

export function esc(str) {
  const d = document.createElement("div");
  d.textContent = str;
  return d.innerHTML;
}

// Attribute-position escape: esc() covers <>& (textContent round-trip) but NOT
// quotes, so `attr="${esc(x)}"` lets a value containing `"` break out of the
// attribute. Use THIS for every dynamic value interpolated into an HTML
// attribute (canonical home for the per-module copies that migrated here).
export function escAttr(str) {
  return esc(str).replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

export function scrollChat() {
  const el = refs.chatStream || document.getElementById('chatStream');
  if (!el) return;
  requestAnimationFrame(() => {
    const el2 = refs.chatStream || document.getElementById('chatStream');
    if (el2) el2.scrollTop = el2.scrollHeight;
  });
}

// Pure predicate: is the scroll position within `threshold` px of the bottom?
// Extracted from the stick-to-bottom decision (main.js F2) so it is unit-testable
// without a DOM (scroll-helpers.test.mjs). `scrollHeight - clientHeight` is the
// maximum scrollTop (fully scrolled); a remaining gap <= threshold counts as
// "at the bottom". Non-overflowing content (gap <= 0) always counts as at bottom.
export function isNearBottom(scrollTop, clientHeight, scrollHeight, threshold = 120) {
  return (scrollHeight - clientHeight - scrollTop) <= threshold;
}

const DEFAULT_PLACEHOLDER = "Ask AgentCPQ anything, @ to add files, / for commands";

export function lockInput() {
  state.inputLocked = true;
  const input = refs.promptInput || document.getElementById('promptInput');
  const btn = refs.btnSend || document.getElementById('btnSend');
  if (input) {
    input.disabled = true;
    input.placeholder = "Please wait\u2026";
  }
  if (btn) btn.disabled = true;
}

export function unlockInput() {
  state.inputLocked = false;
  const input = refs.promptInput || document.getElementById('promptInput');
  const btn = refs.btnSend || document.getElementById('btnSend');
  if (input) {
    input.disabled = false;
    input.placeholder = DEFAULT_PLACEHOLDER;
  }
  if (btn && input) {
    btn.disabled = !input.value.trim();
  }
}

// ---------------------------------------------------------------------------
// Panel loading bar
// ---------------------------------------------------------------------------

/**
 * Show or hide the indeterminate loading bar at the top of a panel.
 * @param {string} panelId — panel-registry id (e.g. 'explorer', 'sync-status')
 * @param {boolean} loading — true to show, false to hide
 */
export function setPanelLoading(panelId, loading) {
  const container = document.getElementById(`panel-${panelId}`);
  if (!container) return;
  let bar = container.querySelector('.panel-loading-bar');
  if (loading) {
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'panel-loading-bar';
      container.prepend(bar);
    }
    // Force reflow then activate for CSS transition
    void bar.offsetWidth;
    bar.classList.add('active');
  } else if (bar) {
    bar.classList.remove('active');
  }
}

export function getSyncStatus(kind, name) {
  return state.syncStatusMap.get(`${kind}/${name}`);
}

// Sync-direction → Lucide icon class. Color comes from .sync-badge.<dir> in styles.css.
const SYNC_ICON = {
  in_sync:     "check",
  local_only:  "arrow-up",
  remote_only: "arrow-down",
  conflict:    "alert-triangle",
  local:       "circle",
};

export function syncBadgeHtml(status) {
  if (!status || !status.direction) return "";
  const dir = status.direction;
  const name = SYNC_ICON[dir];
  const ico = name ? `<i class="ic ic-sm ic-${name}"></i>` : "";
  return `<span class="sync-badge ${dir}" title="${dir.replace("_", " ")}">${ico}</span>`;
}

// File-type icon — single Lucide glyph (file-code) coloured by language class in styles.css.
export function fileIconSvg(lang) {
  const cls = lang === "python" ? "file-icon-py"
    : lang === "json"   ? "file-icon-json"
    : lang === "html"   ? "file-icon-html"
    : "file-icon-generic";
  return `<i class="ic ic-file-code ws-tab-icon file-icon ${cls}"></i>`;
}

export function folderIconSvg() {
  return `<i class="ic ic-folder file-icon-folder"></i>`;
}

// ---------------------------------------------------------------------------
// Pinned artifacts — localStorage persistence (per-tenant key)
// ---------------------------------------------------------------------------

function pinnedKey() {
  return `agentcpq_pinned:${state.tenant || "default"}`;
}

export function loadPinnedArtifacts() {
  try {
    const raw = localStorage.getItem(pinnedKey());
    if (!raw) { state.pinnedArtifacts = new Set(); return; }
    const arr = JSON.parse(raw);
    state.pinnedArtifacts = new Set(Array.isArray(arr) ? arr : []);
  } catch {
    state.pinnedArtifacts = new Set();
  }
}

export function savePinnedArtifacts() {
  try {
    localStorage.setItem(pinnedKey(), JSON.stringify([...state.pinnedArtifacts]));
  } catch { /* quota exceeded — ignore */ }
}

export function pinKey(kind, name) { return `${kind}:${name}`; }

export function isArtifactPinned(kind, name) {
  return state.pinnedArtifacts.has(pinKey(kind, name));
}

export function togglePinArtifact(kind, name) {
  const k = pinKey(kind, name);
  if (state.pinnedArtifacts.has(k)) state.pinnedArtifacts.delete(k);
  else state.pinnedArtifacts.add(k);
  savePinnedArtifacts();
}
